import { DocumentUpload, Id, Nullable, TFSEntry, type TFSEntryDocumentKey } from "@/models"
import { ApiService } from "@/services"
import type { PlainDate } from "@/temporal"
import { StringSchema } from "vue-utils"

export interface UpdateTFSData {
	validFrom: Nullable<PlainDate>
	validTo: Nullable<PlainDate>
	maximumShipments: Nullable<number>
	maximumActiveShipments: Nullable<number>
	consentIssued: boolean
	consentIssuedDocuments: File[]
	notes: string
}

export interface CreateTFSData extends UpdateTFSData {
	tfsNumber: string
	wkNumber: Nullable<string>
	companyId: Id
}

export class TfsService extends ApiService {
	constructor() {
		super("/api/tfs-entries")
	}

	async get(id: Id): Promise<TFSEntry> {
		return await this.returning(TFSEntry).get(String(id))
	}

	async create(data: CreateTFSData) {
		const formData = this.createCreationFormData(data)
		return await this.returning(TFSEntry).post("", formData)
	}

	async validateCreate(data: CreateTFSData) {
		const formData = this.createCreationFormData({
			...data,
			consentIssuedDocuments: [], // Avoid sending data that's not needed
		})
		return await this.returningVoid().post(`validate`, formData)
	}

	async update(tfsId: Id, data: UpdateTFSData) {
		const formData = this.createUpdateFormData(data)
		return await this.returning(TFSEntry).put(String(tfsId), formData)
	}

	async validateUpdate(tfsId: Id, data: UpdateTFSData) {
		const formData = this.createUpdateFormData({
			...data,
			consentIssuedDocuments: [], // Avoid sending data that's not needed
		})
		return await this.returningVoid().put(`${tfsId}/validate`, formData)
	}

	async close(tfsId: Id, closingDocuments: File[]) {
		const formData = new FormData()
		for (const file of closingDocuments) {
			formData.append("ClosingDocuments", file)
		}
		return await this.returning(TFSEntry).post(`${tfsId}/close`, formData)
	}

	async delete(tfsId: Id) {
		return await this.returningVoid().delete(String(tfsId))
	}

	async uploadAdditionalDocuments(tfsId: Id, documents: File[]) {
		const formData = new FormData()
		for (const file of documents) {
			formData.append("AdditionalDocuments", file)
		}
		return await this.returning(DocumentUpload.array()).put(`${tfsId}/additional-documents`, formData)
	}

	async deleteDocument(key: TFSEntryDocumentKey, documentId: string) {
		return await this.returningVoid().delete(`documents/${key}/${documentId}`)
	}

	async getRecipientSuggestions(tfsId: Id) {
		return await this.returning(StringSchema.array()).get(`${tfsId}/recipient-suggestions`)
	}

	private createCreationFormData(creationData: CreateTFSData): FormData {
		const formData = this.createUpdateFormData(creationData)
		formData.set("TFSNumber", creationData.tfsNumber)
		formData.set("CompanyId", String(creationData.companyId))
		if (creationData.wkNumber) {
			formData.set("WKNumber", creationData.wkNumber)
		}
		return formData
	}

	private createUpdateFormData(saveData: UpdateTFSData): FormData {
		const formData = new FormData()
		if (saveData.validFrom) {
			formData.set("ValidFrom", saveData.validFrom.toJSON())
		}
		if (saveData.validTo) {
			formData.set("ValidTo", saveData.validTo.toJSON())
		}
		formData.set("ConsentIssued", String(saveData.consentIssued))
		if (saveData.maximumShipments !== null) {
			formData.set("MaximumShipments", String(saveData.maximumShipments))
		}
		if (saveData.maximumActiveShipments !== null) {
			formData.set("MaximumActiveShipments", String(saveData.maximumActiveShipments))
		}
		if (saveData.notes) {
			formData.set("Notes", saveData.notes)
		}

		for (const file of saveData.consentIssuedDocuments) {
			formData.append("ConsentIssuedDocuments", file)
		}
		return formData
	}
}
